/**
 * Renders the entire route table from the central registry.
 *
 * Why this component exists:
 *  - App.tsx used to declare ~30 hand-written `<Route>` elements, each
 *    one wrapping its page in an `<ErrorBoundary>` with a custom label.
 *    That was easy to get wrong (missing boundary, mismatched context,
 *    forgotten role guard).
 *  - The registry in `@/features` now declares everything. This file
 *    walks the registry and builds the react-router tree, applying
 *    `<ProtectedRoute>`, `<RoleGuard>`, `<FeatureGate>` and the per-page
 *    `<ErrorBoundary>` consistently.
 *
 * Adding a new route does **not** touch this file.
 */

import { Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Layout } from '@/components/Layout';
import { FlowLayoutRouter } from '@/components/layouts/FlowLayoutRouter';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ComingSoon } from '@/components/ComingSoon';
import { useEffectiveRole } from '@/hooks/useEffectiveRole';
import { useFeatures } from '@/hooks/useFeatures';
import { isEnabled } from '@/config/feature-flags';

import { ROUTES, REDIRECTS } from '@/features/route-registry';
import type { RouteModule, Role } from '@/features/types';
import NotFound from '@/pages/NotFound';

// ─── Helpers ──────────────────────────────────────────────────────────────

const PageLoader = () => (
  <div className="flex items-center justify-center h-[60vh]">
    <Loader2 className="w-6 h-6 animate-spin text-primary" />
  </div>
);

/** Wrap a node in an `<ErrorBoundary>` unless the route opts out. */
function withErrorBoundary(route: RouteModule, node: ReactNode): ReactNode {
  if (route.noErrorBoundary) return node;
  return (
    <ErrorBoundary context={route.errorContext ?? route.id}>{node}</ErrorBoundary>
  );
}

interface RoleGuardProps {
  allowed: readonly Role[] | undefined;
  children: ReactNode;
}

/**
 * Hides a page from users who lack the required role. Renders a friendly
 * "no access" message instead of silently 404-ing so users understand
 * the page exists but is not for them.
 *
 * Im Support-Modus liefert `useEffectiveRole()` für Plattform-Admins
 * die Org-Admin-Rolle der Ziel-Org (siehe Hook-Header), wodurch der Guard
 * die Workflow-Routen freigibt — Schreibzugriffe bleiben dennoch durch
 * den Repository-Layer geblockt.
 */
function RoleGuard({ allowed, children }: RoleGuardProps) {
  const role = useEffectiveRole();
  const { t } = useTranslation();
  if (!allowed || allowed.length === 0) return <>{children}</>;
  if (role && allowed.includes(role)) return <>{children}</>;
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-8 text-center">
      <div className="max-w-sm space-y-2">
        <h2 className="text-lg font-semibold text-foreground">{t('common.noAccess')}</h2>
        <p className="text-sm text-muted-foreground">{t('common.noAccessHint')}</p>
      </div>
    </div>
  );
}

interface FeatureGateProps {
  feature: RouteModule['feature'];
  children: ReactNode;
}

/**
 * Friendly "this surface is not available" notice. Shared by the per-org
 * `<FeatureGate>` and the static MVP kill switch so a disabled deep link
 * degrades to a calm placeholder instead of a crash or a 404.
 */
function FeatureUnavailableNotice() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-8 text-center">
      <div className="max-w-sm space-y-2">
        <h2 className="text-lg font-semibold text-foreground">{t('common.featureDisabled')}</h2>
        <p className="text-sm text-muted-foreground">{t('common.featureDisabledHint')}</p>
      </div>
    </div>
  );
}

/**
 * Hides a page when the organization has not enabled the underlying
 * feature flag. Useful so deep links from older bookmarks degrade
 * gracefully instead of crashing.
 */
function FeatureGate({ feature, children }: FeatureGateProps) {
  const { hasFeature } = useFeatures();
  if (!feature) return <>{children}</>;
  if (hasFeature(feature)) return <>{children}</>;
  return <FeatureUnavailableNotice />;
}

/**
 * Build the inner element for a single route, layering on access checks
 * and the page-level error boundary in a fixed order:
 *
 *   ErrorBoundary > RoleGuard > FeatureGate > ComingSoonGate > <Page />
 *
 * The boundary is outermost so errors *inside* the gate render the
 * fallback UI instead of bubbling to the global boundary.
 *
 * `comingSoon` short-circuits everything below it: when a route is
 * flagged the actual page component is *never* mounted, which is what
 * makes the flag safe to use on half-finished pages — there is no risk
 * of the underlying component crashing.
 */
function renderElement(route: RouteModule): ReactNode {
  // Static MVP kill switch (src/config/feature-flags.ts): a disabled
  // module never mounts its page component. We still wrap the placeholder
  // in the page error boundary so the shell stays intact.
  if (route.featureFlag && !isEnabled(route.featureFlag)) {
    return withErrorBoundary(route, <FeatureUnavailableNotice />);
  }
  const Component = route.component;
  let node: ReactNode = route.comingSoon
    ? <ComingSoon note={route.comingSoonNote} />
    : <Component />;
  if (route.feature && !route.comingSoon) {
    node = <FeatureGate feature={route.feature}>{node}</FeatureGate>;
  }
  if (route.roles && route.roles.length > 0) {
    node = <RoleGuard allowed={route.roles}>{node}</RoleGuard>;
  }
  return withErrorBoundary(route, node);
}

// ─── Renderer ─────────────────────────────────────────────────────────────

export function AppRoutes() {
  // Split routes by mounting strategy so the JSX stays flat and obvious.
  const protectedRoutes = ROUTES.filter((r) => (r.kind ?? 'protected') === 'protected');
  const publicRoutes = ROUTES.filter((r) => r.kind === 'public');
  const publicWithLayoutRoutes = ROUTES.filter((r) => r.kind === 'public-with-layout');

  return (
    <Suspense fallback={<PageLoader />}>
      {/*
       * Bereich (`.area-*`) und Modus (`.mode-*`) werden vom AreaProvider /
       * ModeProvider in App.tsx aufs <html> gespiegelt — kein Side-Effect-
       * Component mehr nötig.
       */}
      <Routes>
        {/* Public, no layout */}
        {publicRoutes.map((route) => (
          <Route
            key={route.id}
            path={route.path}
            element={renderElement(route)}
          />
        ))}

        {/* Public, but inside the app shell */}
        {publicWithLayoutRoutes.length > 0 && (
          <Route element={<Layout />}>
            {publicWithLayoutRoutes.map((route) => (
              <Route
                key={route.id}
                path={route.path}
                element={renderElement(route)}
              />
            ))}
          </Route>
        )}

        {/* Protected — auth + layout. `FlowLayoutRouter` is a transparent
            drop-in for <Layout>: it renders the classic sidebar shell unless
            the org has `ui_flow_mode` enabled and the route is a Flow route. */}
        <Route element={<ProtectedRoute><FlowLayoutRouter /></ProtectedRoute>}>
          {protectedRoutes.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              element={renderElement(route)}
            />
          ))}
        </Route>

        {/* Static redirects (e.g. /documents -> /patients) */}
        {REDIRECTS.map(({ from, to }) => (
          <Route key={`redirect-${from}`} path={from} element={<Navigate to={to} replace />} />
        ))}

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
